/**
 * B521 — the product page's two new instruments (Figma 364-2, "Storefront —
 * Product page @1440"): the photo carousel over `presentation.photos[]`, and
 * the month calendar with a price under a day. Both are chrome over facts a
 * read carried — the carousel never invents a picture, and the calendar
 * prints only the prices its caller hands it (a flat price list prints one
 * number, under the selected day; never a fabricated per-day price).
 */
import { useId, useState, type ReactNode } from "react";
import { sizedPhotoUrl } from "@transact/domain";
import { Button, Input } from "./components.js";

/** B521 — re-exported so a surface sizes asset-host photos without its own domain dependency. */
export { sizedPhotoUrl };

export interface CarouselPhoto {
  readonly url: string;
  /** What the picture shows; absent = decorative (the product's name is already on the page). */
  readonly alt?: string;
  /** Who took it, when the tenant must say so. */
  readonly credit?: string;
}

/**
 * The photo carousel (364-2 frame 364:10): one picture at a time, ‹ › doors
 * and a dot per photo, the caption in the kicker register ("PHOTO · SUNSET
 * CRUISE — THE OPEN DECK"). One photo draws no doors and no dots — a door
 * that goes nowhere is a lie. The dots are real buttons (a 44px reach
 * around a 10px face), each named "Photo 2 of 3" and pressed when shown.
 */
export function PhotoCarousel(props: {
  readonly photos: readonly CarouselPhoto[];
  /** The product's name — the caption's subject and the region's name. */
  readonly subject: string;
  /** Start on this photo (tests, deep links); default the first. */
  readonly initialIndex?: number;
  readonly redline?: string;
  /** B614 (555:2) — short facts drawn over the photo's top-left (a show's "21+", "2-drink minimum"); the caller's words. */
  readonly badges?: readonly string[];
}): ReactNode {
  const count = props.photos.length;
  const [index, setIndex] = useState(() => Math.min(Math.max(props.initialIndex ?? 0, 0), Math.max(count - 1, 0)));
  const captionId = useId();
  if (count === 0) return null;
  const photo = props.photos[index] ?? props.photos[0]!;
  const go = (next: number): void => setIndex(((next % count) + count) % count);
  const caption = [
    /* B614 — an alt that only repeats the subject is said once (measured on dev: "Cedric — Cedric"). */
    `Photo · ${props.subject}${photo.alt !== undefined && photo.alt.trim() !== props.subject.trim() ? ` — ${photo.alt}` : ""}`,
    ...(photo.credit !== undefined ? [`© ${photo.credit}`] : []),
  ].join(" · ");
  return (
    <figure
      className="t-carousel"
      aria-roledescription="carousel"
      aria-label={`Photos of ${props.subject}`}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      <img className="t-carousel-img" src={sizedPhotoUrl(photo.url, 1200)} alt={photo.alt ?? ""} aria-describedby={captionId} />
      {props.badges !== undefined && props.badges.length > 0 ? (
        <ul className="t-carousel-badges">
          {props.badges.map((b) => (
            <li key={b} className="t-carousel-badge">
              {b}
            </li>
          ))}
        </ul>
      ) : null}
      {count > 1 ? (
        <>
          <button type="button" className="t-carousel-door t-carousel-door--prev" aria-label="Previous photo" onClick={() => go(index - 1)}>
            <span aria-hidden="true">‹</span>
          </button>
          <button type="button" className="t-carousel-door t-carousel-door--next" aria-label="Next photo" onClick={() => go(index + 1)}>
            <span aria-hidden="true">›</span>
          </button>
        </>
      ) : null}
      <figcaption className="t-carousel-foot">
        <span className="t-carousel-caption" id={captionId}>
          {caption}
        </span>
        {count > 1 ? (
          <span className="t-carousel-dots">
            {props.photos.map((p, i) => (
              <button
                key={`${i}:${p.url}`}
                type="button"
                className="t-carousel-dot"
                aria-label={`Photo ${i + 1} of ${count}`}
                aria-pressed={i === index}
                onClick={() => go(i)}
              />
            ))}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}

export interface CalendarDay {
  /** The caller's own day key (the same key its day state uses). */
  readonly key: string;
  readonly dayOfMonth: number;
  /** The whole day in words for assistive tech — "Tuesday 22 September". */
  readonly label: string;
  /** False = drawn muted and not pressable (nothing on sale that day). */
  readonly available: boolean;
  /** The price under the day, already formatted; absent = no number drawn. */
  readonly price?: string;
}

/**
 * The month calendar (364-2 frame 364:47): the kicker ("PICK A DAY"), the
 * month as the title, month doors where the caller has somewhere to go,
 * the weekday row, and one cell per day — pressable where something is on
 * sale, muted where nothing is. A price sits under a day only when the
 * caller hands one.
 */
export function MonthCalendar(props: {
  readonly kicker: string;
  /** "September 2026". */
  readonly title: string;
  /** Blank cells before day 1 — 0 = the month starts on the first column. */
  readonly leadingBlanks: number;
  readonly days: readonly CalendarDay[];
  readonly selectedKey?: string;
  readonly onSelect: (key: string) => void;
  /** Absent = no earlier month has anything on sale; no door is drawn. */
  readonly onPrevMonth?: () => void;
  readonly onNextMonth?: () => void;
  /** Column heads, first column first. Default Sunday-first. */
  readonly weekdays?: readonly string[];
  /** One line under the grid that teaches the prices ("One price every day"). */
  readonly legend?: string;
  readonly redline?: string;
}): ReactNode {
  const kickerId = useId();
  const weekdays = props.weekdays ?? ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const blanks = Array.from({ length: Math.max(0, Math.min(6, props.leadingBlanks)) }, (_, i) => i);
  return (
    <section className="t-monthcal" aria-labelledby={kickerId} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <header className="t-monthcal-head">
        <p className="t-monthcal-kicker" id={kickerId}>
          {props.kicker}
        </p>
        <span className="t-monthcal-nav">
          {props.onPrevMonth !== undefined ? (
            <button type="button" className="t-monthcal-door" aria-label="Previous month" onClick={props.onPrevMonth}>
              <span aria-hidden="true">‹</span>
            </button>
          ) : null}
          <span className="t-monthcal-title">{props.title}</span>
          {props.onNextMonth !== undefined ? (
            <button type="button" className="t-monthcal-door" aria-label="Next month" onClick={props.onNextMonth}>
              <span aria-hidden="true">›</span>
            </button>
          ) : null}
        </span>
      </header>
      <ol className="t-monthcal-weekdays" aria-hidden="true">
        {weekdays.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ol>
      <ol className="t-monthcal-grid">
        {blanks.map((b) => (
          <li key={`blank-${b}`} className="t-monthcal-blank" aria-hidden="true" />
        ))}
        {props.days.map((d) => (
          <li key={d.key}>
            {d.available ? (
              <button
                type="button"
                className="t-monthcal-day"
                aria-pressed={d.key === props.selectedKey}
                aria-label={d.price !== undefined ? `${d.label}, ${d.price}` : d.label}
                onClick={() => props.onSelect(d.key)}
              >
                <span className="t-monthcal-num">{d.dayOfMonth}</span>
                {d.price !== undefined ? <span className="t-monthcal-price">{d.price}</span> : null}
              </button>
            ) : (
              <span className="t-monthcal-day t-monthcal-day--off">
                <span className="t-monthcal-num">{d.dayOfMonth}</span>
              </span>
            )}
          </li>
        ))}
      </ol>
      {props.legend !== undefined ? <p className="t-monthcal-legend">{props.legend}</p> : null}
    </section>
  );
}

/* ------------------------------------------------------------ PhotoTiles */

export interface PhotoTile {
  readonly url: string;
  /** What the picture shows ("" = not described yet). */
  readonly alt: string;
}

/** The three photo types the asset host takes (B521 ruling). */
export const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";

/**
 * B521 — the back office's photo editor for one product. A tile per stored
 * photo: the thumbnail, a labeled field for what it shows (the buyer's alt
 * text), and doors to move it and remove it. The first tile says, on its
 * face, that it is the one on the product card — the fact an operator
 * reorders for. A door that goes nowhere is ABSENT, not shut (the first
 * cannot move earlier). ONE add zone takes a drop or a file choice; at the
 * cap it is replaced by the sentence saying why, and a caller that cannot
 * add yet passes `addWhy` instead of `onAdd` — the reason is drawn where the
 * zone would be. An upload in flight wears the working face and names the
 * act in a live region; a refusal sits at the field and the file control
 * points at it.
 */
export function PhotoTiles(props: {
  readonly id: string;
  readonly label: string;
  readonly photos: readonly PhotoTile[];
  /** The cap (the contract's ≤6). */
  readonly max: number;
  /** Reorder, remove and alt edits — the whole new list. */
  readonly onChange: (photos: readonly PhotoTile[]) => void;
  /** Present = the operator may add; the files chosen or dropped, in order. */
  readonly onAdd?: (files: readonly File[]) => void;
  /** Why adding is not possible yet (drawn where the zone would be) — used when `onAdd` is absent. */
  readonly addWhy?: string;
  /** An upload is in the air. */
  readonly busy?: boolean;
  readonly error?: string | undefined;
  /** The thumbnail's URL for a stored photo (the asset host sizes it); default the stored URL. */
  readonly thumbUrl?: (url: string) => string;
  /**
   * B611 — photos uploaded in this sitting that the caller has not saved yet
   * (by URL): each wears JUST ADDED until the caller's save lands.
   */
  readonly fresh?: readonly string[];
}): ReactNode {
  const [dragging, setDragging] = useState(false);
  const errorId = `${props.id}-error`;
  const hintId = `${props.id}-hint`;
  const whyId = `${props.id}-why`;
  const fileId = `${props.id}-file`;
  const busy = props.busy === true;
  const thumb = props.thumbUrl ?? ((u: string) => u);
  const move = (from: number, to: number): void => {
    const next = [...props.photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    props.onChange(next);
  };
  const full = props.photos.length >= props.max;
  const take = (list: FileList | null): void => {
    if (list === null || props.onAdd === undefined || busy) return;
    const files = Array.from(list).slice(0, props.max - props.photos.length);
    if (files.length > 0) props.onAdd(files);
  };
  return (
    <fieldset className="t-fieldset t-photo-tiles">
      <legend>{props.label}</legend>
      {props.photos.length > 0 ? (
        <ol className="t-photo-tiles-list">
          {props.photos.map((photo, i) => (
            <li key={photo.url} className="t-photo-tile">
              <img className="t-photo-tile-img" src={thumb(photo.url)} alt={photo.alt} />
              {/* B611 (551:136) — the first is the CARD PICTURE; a fresh upload says it is not saved yet */}
              {i === 0 ? <span className="t-photo-tile-badge">Card picture</span> : null}
              {props.fresh?.includes(photo.url) === true ? (
                <span className="t-photo-tile-badge t-photo-tile-badge--fresh">Just added</span>
              ) : null}
              <Input
                id={`${props.id}-alt-${i}`}
                label={`What photo ${i + 1} shows`}
                value={photo.alt}
                placeholder="The boat at dusk"
                hint="Read aloud to buyers who cannot see the photo."
                onChange={(alt) => props.onChange(props.photos.map((p, j) => (j === i ? { ...p, alt } : p)))}
              />
              <span className="t-photo-tile-doors">
                {i > 0 ? (
                  <Button variant="ghost" ariaLabel={`Move photo ${i + 1} earlier`} onClick={() => move(i, i - 1)}>
                    ‹ Earlier
                  </Button>
                ) : null}
                {i < props.photos.length - 1 ? (
                  <Button variant="ghost" ariaLabel={`Move photo ${i + 1} later`} onClick={() => move(i, i + 1)}>
                    Later ›
                  </Button>
                ) : null}
                <Button
                  variant="textlink-danger"
                  ariaLabel={`Remove photo ${i + 1}`}
                  onClick={() => props.onChange(props.photos.filter((_, j) => j !== i))}
                >
                  Remove
                </Button>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {full ? (
        <p className="t-field-hint">{`A product shows at most ${props.max} photos. Remove one to add another.`}</p>
      ) : props.onAdd === undefined ? (
        <p className="t-gate-note" id={whyId}>
          {props.addWhy ?? "Photos cannot be added here."}
        </p>
      ) : (
        <div
          className={`t-photo-drop${dragging ? " t-photo-drop--over" : ""}`}
          aria-busy={busy || undefined}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            take(e.dataTransfer.files);
          }}
        >
          <input
            id={fileId}
            className="t-photo-drop-input"
            type="file"
            accept={PHOTO_ACCEPT}
            multiple
            aria-describedby={props.error !== undefined ? `${hintId} ${errorId}` : hintId}
            {...(props.error !== undefined ? { "aria-invalid": true } : {})}
            onChange={(e) => {
              take(e.currentTarget.files);
              e.currentTarget.value = "";
            }}
          />
          <label className="t-photo-drop-label" htmlFor={fileId}>
            <span className="t-photo-drop-title">{busy ? "Uploading…" : "Add photos"}</span>
            <span className="t-field-hint" id={hintId}>
              {`Drop them here or choose files — JPEG, PNG or WebP, up to 5 MB each. ${props.max - props.photos.length} more fit.`}
            </span>
          </label>
          {busy ? (
            <p className="t-visually-hidden" role="status">
              Uploading the photo…
            </p>
          ) : null}
        </div>
      )}
      {props.error !== undefined ? (
        <p className="t-field-error" id={errorId}>
          {props.error}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * B606 (f) — an email drawn the way an inbox draws it: the From and Subject
 * an inbox list shows, over the message itself in a SANDBOXED frame (no
 * scripts, no same-origin, no forms, no top navigation), so a tenant's words
 * can never act on the back office around them. The caller hands in the
 * EXACT HTML the sender renders — the preview and the send are one renderer
 * with two callers, never a lookalike (the B403 §4 failure).
 */
export function EmailPreview(props: {
  /** What the frame is, for assistive tech ("Preview of your ticket email"). */
  readonly title: string;
  readonly html: string;
  readonly subject?: string | undefined;
  readonly from?: string | undefined;
}): ReactNode {
  return (
    <figure className="t-email-preview">
      {props.from === undefined && props.subject === undefined ? null : (
        <figcaption className="t-email-preview-head">
          {props.from === undefined ? null : <span className="t-email-preview-from">{props.from}</span>}
          {props.subject === undefined ? null : <span className="t-email-preview-subject">{props.subject}</span>}
        </figcaption>
      )}
      <iframe className="t-email-preview-frame" title={props.title} sandbox="" srcDoc={props.html} />
    </figure>
  );
}
